/**
 * Divisore dei task (7/10/2026).
 *
 * Nella finestra Nuovo task si possono scrivere più cose insieme, una per
 * riga: qui ogni riga diventa un task, con cliente, persona, scadenza e
 * urgenza presi dal testo. È uno script, senza AI, e la regola è una sola:
 * quello che non è sicuro diventa un dubbio da mostrare nell'anteprima, mai un
 * dato messo in silenzio. Una persona sbagliata riceve mail e Discord veri, un
 * cliente sbagliato finisce nei conti del cliente sbagliato.
 *
 * Come legge una riga:
 *  - il puntino d'elenco si toglie ("-", "•", "1.", "2)", "- [ ]");
 *  - una riga che dice solo una data, una persona o un cliente ("domani:",
 *    "lorenzo:", "graci:") non è un task: vale per le righe sotto;
 *  - cliente: parole intere, senza accenti né maiuscole. Una parola sola basta
 *    se è distintiva (graci, cospin, emmequadro); le parole di tutti i giorni
 *    (studio, rosa, case, auto) contano solo dentro la frase ("la rosa",
 *    "studio irregolare"). Un nome di battesimo da solo non sceglie il
 *    cliente, tranne quello di un cliente che è una persona e che nessun
 *    altro ha ("fausto" per Fausto Carbone). Dai progetti vale la parte prima
 *    di "—" ("Villa Fago");
 *  - persona: solo con un segnale chiaro, all'inizio ("x lorenzo:",
 *    "lorenzo:", "lorenzo deve", "andrea,") o in fondo ("(lorenzo)"), oppure
 *    "@lore". "chiedere a lorenzo" non assegna niente. Se il nome è quello di
 *    un cliente, vince il cliente;
 *  - scadenza: oggi, domani, i giorni della settimana, "il 5", "20/12",
 *    "15 gennaio", "fine mese", "tra 2 settimane". Una data che non esiste o
 *    che è già passata è un dubbio, mai un'altra data;
 *  - urgenza: "urgente", "asap", "priorità alta"; "non urgente" no.
 * Il titolo perde persona, data e urgenza; il nome del cliente resta.
 */
/** Il server accetta titoli fino a 200 caratteri (createTaskInput). */
export const TITOLO_MAX = 200;
/**
 * Dubbio di una riga che dice solo una data, una persona o un cliente e non ha
 * righe sotto ("venerdì", "lorenzo" da soli): l'anteprima la lascia senza spunta.
 */
export const DUBBIO_MANCA_COSA_FARE = 'Questa riga non dice cosa fare';
/** Dubbio di un titolo tagliato: la finestra mette la riga intera nella descrizione. */
export const DUBBIO_TITOLO_ACCORCIATO = 'Titolo accorciato a 200 caratteri: la riga intera va nella descrizione';
/** Oggi a Roma, 'YYYY-MM-DD': il campo `oggi` del contesto. */
export function oggiARoma(adesso = new Date()) {
    const parti = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(adesso);
    const v = (t) => parti.find((p) => p.type === t)?.value ?? '';
    return `${v('year')}-${v('month')}-${v('day')}`;
}
// ── Parole che da sole non dicono quale cliente ───────────────────────────
const insieme = (s) => new Set(s.split(/\s+/).filter(Boolean));
/** Articoli, preposizioni, congiunzioni. */
const VUOTE = insieme(`
  di del della dello dei degli delle dell dal dalla dallo dai dagli dalle dall da de d e ed and
  a al alla allo ai agli alle all ad in nel nella nello nei negli nelle con col su sul sulla sui
  per tra fra il lo la i gli le l un uno una the of for to at on o or`);
/** Davanti a un nome ne fanno parte: "la rosa", "the rebel", "de fiore". */
const ARTICOLI_DEL_NOME = insieme('la lo il le the de');
/** Particelle dei cognomi: "De Fiore", "Di Franco", "La Manna". */
const PARTICELLE = insieme('de di la lo del della d');
/** Forme societarie: nessuno le scrive per dire il cliente. */
const FORME = insieme(`
  srl srls sas snc spa ss sapa scarl scrl sarl onlus aps asd ssd coop cooperativa societa
  responsabilita limitata semplificata benefit gmbh ltd llc inc sl bv ou`);
const TITOLI = insieme(`
  dott dottor dottore dottssa dottoressa dssa ssa dr ing avv arch geom rag prof profssa
  professore professoressa sig sigra signor signora mr mrs`);
/**
 * Che tipo di attività è: in una frase di sole parole così ("centro medico",
 * "studio associato", "costruzioni edili") non c'è un nome.
 */
const GENERICHE = insieme(`
  azienda aziende agricola agricolo agricole impresa imprese ditta societa studio studi associato
  associati associazione centro centri medico medica medici medical clinica clinic cliniche
  poliambulatorio ambulatorio fondazione istituto universitario universitaria universita ospedale
  ospedaliera farmacia farmacie gioielleria gioiellerie oreficeria gioielli costruzioni costruzione
  edile edili edilizia group gruppo holding store stores shop service services servizi servizio
  consulting consulenza consulenze consulenti partners partner solutions soluzioni lab labs
  laboratorio laboratori training formazione academy accademia scuola professional professionals
  management marketing comunicazione communication communications agency agenzia digital media web
  design travel viaggi tours tour excursions escursioni hotel hotels resort residence dimora dimore
  antica antico case casa house home apartments appartamenti affittacamere ristorante trattoria
  pizzeria bar cafe caffe pasticceria panificio cantina cantine vini vino wine wines winery tenuta
  tenute masseria agriturismo fattoria auto automobili moto motors car cars garage officina ricambi
  trasporti logistica immobiliare immobili properties property real estate sport sports padel
  fitness gym palestra club beauty estetica estetico salone abbigliamento moda fashion boutique
  informatica tecnologie technology technologies tech systems sistemi software energia energy
  impianti elettrica idraulica ingegneria ingegneri architettura architetti avvocati legale
  commercialista commercialisti notaio assicurazioni broker finance finanza investimenti capital
  leasing credito banca international internazionale network distribuzione commercio commerciale
  import export industria industrie industriale produzione trading company enterprise innovation
  innovazione foundry factory laser irccs banqueting catering eventi events`);
/** Parole di tutti i giorni, che stanno anche nei nomi dei clienti ("nessuna urgenza", "la stampante in studio"). */
const COMUNI = insieme(`
  rosa rose fiore fiori nessuna nessuno semplice irregolare talent job permanent new top run sea sky
  blue green red white black gold oro silver water complements advisor rebel swag rollout building
  figlio figli fratelli eredi sorelle compagnia mare sole luna stella stelle acqua terra fuoco aria
  vento monte poggio colle valle piano porto isola lago fiume bosco giardino orto villa ville palazzo
  torre castello borgo corte vecchia vecchio nuovo nuova nuovi nuove bella bello belle grande grandi
  piccolo piccola primo prima dolce buono buona chiaro chiara gentile giovane giovani natura natural
  eco bio smart plus pro prime premium best one first life style living food drink trasformazione
  orizzonti orizzonte mediterraneo mediterranea ecologiche ecologico sud nord est ovest centrale
  generale nazionale regionale world global europe euro italia italiana italiano italy sicilia
  siciliana sicily post sito siti social video foto reel reels logo mail email newsletter fattura
  fatture preventivo preventivi contratto contratti campagna campagne grafica grafiche ads meta dem
  seo sem blog app pagina pagine landing brand cliente clienti progetto progetti task lavoro
  brt dhl gls sda ups tnt fedex amazon google facebook instagram tiktok linkedin youtube whatsapp
  and the for with your our you we all big good great two three`);
const CITTA = insieme(`
  roma milano napoli torino palermo catania messina siracusa ragusa trapani agrigento enna
  caltanissetta acireale taormina giarre riposto paterno adrano bronte misterbianco gravina
  mascalucia aci trecastagni zafferana nicolosi belpasso etna firenze bologna venezia genova bari
  lecce cagliari verona padova trieste brescia bergamo pisa livorno siena perugia ancona pescara
  salerno reggio calabria cosenza sardegna puglia toscana lombardia lazio campania london londra
  paris parigi berlino berlin madrid barcellona amsterdam`);
/** Nomi di battesimo: da soli non dicono quale cliente ("chiedere a franco", "review con michela"). */
const NOMI = insieme(`
  andrea lorenzo marco luca giuseppe giovanni antonio francesco mario luigi paolo roberto stefano
  alessandro alessio davide daniele simone matteo federico fabio massimo maurizio claudio sergio
  salvatore sebastiano vincenzo carmelo angelo fausto franco gianni gianluca giorgio enrico emanuele
  samuele gabriele riccardo nicola nicolo pietro piero filippo giacomo alberto damiano cristian
  christian cristiano danilo dario diego domenico edoardo elia ettore fabrizio flavio gaetano
  giulio giuliano ivan leonardo manuel mattia michele mirko nino orazio pasquale raffaele rosario
  sandro silvio tommaso umberto valerio vittorio agostino aldo arturo bruno carlo cesare corrado
  dino elio enzo ernesto eugenio ferdinando gino guido ignazio lino marcello marino mauro nello
  ottavio renato rocco rodolfo rino saverio tiziano ugo valentino walter alfio alfredo attilio
  benedetto biagio calogero carmine ciro concetto gioacchino girolamo placido santo nunzio
  venerando giancarlo gianfranco gianmarco giampaolo pierpaolo emiliano emilio jacopo kevin
  maria anna giulia francesca chiara sara laura valentina alessandra martina elena federica silvia
  giorgia roberta paola monica cristina barbara simona claudia daniela elisa eleonora ilaria irene
  marta michela nadia patrizia rita sabrina serena sofia stefania teresa tiziana veronica ylenia
  ilenia jessica aurora beatrice benedetta camilla caterina cecilia clelia concetta dalila debora
  deborah erica erika emma gaia gloria grazia graziella ines isabella letizia lucia luisa mara
  margherita marina marisa marzia miriam nicoletta noemi ornella pamela rachele rebecca rossella
  sabina samantha sebastiana silvana sonia susanna tatiana vanessa viola virginia agata agnese
  alba alice angela angelica antonella arianna carla carolina cinzia denise donatella elisabetta
  emanuela fabiola flavia franca gabriella gianna giovanna giuseppina ida lara lea lina linda
  loredana lorena lorenza luana lucrezia manuela mariella masha melissa milena nina olga pina
  pinella rosaria rosalba sandra vera vittoria zoe giusy daria carmela valeria`);
/** Una parola che da sola basta a dire il cliente. */
function distintiva(w, squadra) {
    return w.length >= 3 && !/^\d+$/.test(w) && !VUOTE.has(w) && !FORME.has(w) && !TITOLI.has(w)
        && !COMUNI.has(w) && !GENERICHE.has(w) && !CITTA.has(w) && !NOMI.has(w) && !squadra.has(w);
}
// ── Testo: pieghe e parole ────────────────────────────────────────────────
const SPAZI_STRANI = /[\p{Zs}\t\v\f]/gu;
/** Spazi a larghezza zero e BOM: spariscono. */
const INVISIBILI = new RegExp(`[${String.fromCharCode(0x200b, 0xfeff)}]`, 'g');
/**
 * Minuscolo e senza accenti, un carattere per carattere: le posizioni restano
 * quelle del testo vero, così dal titolo si toglie il pezzo giusto.
 */
function piega(s) {
    let out = '';
    for (const c of s) {
        if (c === '’' || c === '‘' || c === '`' || c === '´') {
            out += "'";
            continue;
        }
        const base = c.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
        if (base.length === c.length) {
            out += base;
            continue;
        }
        const low = c.toLowerCase();
        out += low.length === c.length ? low : c;
    }
    return out;
}
const CONGIUNZIONI = insieme('e ed and');
// In ordine: data ISO, giorno/mese/anno, giorno/mese, sigla coi punti ("c.i.p.s.", "s.r.l."), parola, "&".
const RE_TOK = /(?<![\d/.-])(\d{4})-(\d{1,2})-(\d{1,2})(?![\d/.-])|(?<![\d/.-])(\d{1,2})([/.-])(\d{1,2})\5(\d{4}|\d{2})(?![\d/.-])|(?<![\d/])(\d{1,2})\/(\d{1,2})(?![\d/])|([a-z]{1,2}(?:\.[a-z]{1,2})+)\.?(?![a-z0-9])|([a-z0-9]+)|(&)/g;
function tokenizza(norm) {
    const out = [];
    for (const m of norm.matchAll(RE_TOK)) {
        const s = m.index ?? 0;
        const e = s + m[0].length;
        if (m[1])
            out.push({ w: m[0], c: m[0], s, e, tipo: 'd', dd: Number(m[3]), mm: Number(m[2]), aa: Number(m[1]) });
        else if (m[4]) {
            const anno = m[7] ?? '';
            out.push({ w: m[0], c: m[0], s, e, tipo: 'd', dd: Number(m[4]), mm: Number(m[6]), aa: Number(anno) + (anno.length === 2 ? 2000 : 0) });
        }
        else if (m[8])
            out.push({ w: m[0], c: m[0], s, e, tipo: 'd', dd: Number(m[8]), mm: Number(m[9]), aa: null });
        else if (m[10]) {
            const w = m[10].replace(/\./g, '');
            out.push({ w, c: w, s, e: s + m[10].length, tipo: 'p' });
        }
        else if (m[11]) {
            const w = m[11];
            out.push({ w, c: CONGIUNZIONI.has(w) ? '&' : w, s, e, tipo: /^\d+$/.test(w) ? 'n' : 'p' });
        }
        else
            out.push({ w: '&', c: '&', s, e, tipo: 'amp' });
    }
    return out;
}
const maiuscola = (s) => (s ? s.charAt(0).toLocaleUpperCase('it-IT') + s.slice(1) : s);
const NUMERI_IN_LETTERE = ['zero', 'un', 'due', 'tre', 'quattro', 'cinque', 'sei'];
const quanti = (n) => NUMERI_IN_LETTERE[n] ?? String(n);
/** "Cospin e Edilap", "A, B e C". */
function elenco(voci) {
    if (voci.length <= 1)
        return voci.join('');
    return `${voci.slice(0, -1).join(', ')} e ${voci[voci.length - 1]}`;
}
/** Le parti di un nome (separate da virgole, parentesi, " - "), senza forme societarie e titoli. */
function partiDelNome(nome) {
    const norm = piega(nome.replace(SPAZI_STRANI, ' '));
    let prima = null;
    const parti = [];
    for (const pezzo of norm.split(/[,;()[\]|/]|\s[-–—]\s|[–—]/)) {
        const t = tokenizza(pezzo).filter((x) => x.tipo !== 'd').map((x) => x.c);
        if (prima === null)
            prima = t.find((x) => x !== '&') ?? null;
        const tenute = [];
        for (let i = 0; i < t.length; i++) {
            const w = t[i];
            if (w === 'societa' && t[i + 1] === 'semplice') {
                i++;
                continue;
            }
            // "a s.u." in fondo, "& C." prima della forma o in fondo
            if (w === 'a' && t[i + 1] === 'su' && i + 2 >= t.length) {
                i++;
                continue;
            }
            if (w === '&' && t[i + 1] === 'c' && (i + 2 >= t.length || FORME.has(t[i + 2]))) {
                i++;
                continue;
            }
            if (FORME.has(w) || TITOLI.has(w))
                continue;
            tenute.push(w);
        }
        while (tenute.length && (VUOTE.has(tenute[tenute.length - 1]) || tenute[tenute.length - 1] === '&'))
            tenute.pop();
        while (tenute.length && (tenute[0] === '&' || (VUOTE.has(tenute[0]) && !ARTICOLI_DEL_NOME.has(tenute[0]))))
            tenute.shift();
        if (tenute.length)
            parti.push(tenute);
    }
    return { parti, prima };
}
/**
 * Le frasi con cui si può dire un cliente, da una parte del suo nome.
 * `titolare`: in "Etna & Sea Excursions di Laura Marinelli" il cognome dopo
 * "di" da solo non basta (le persone si nominano anche per altro), serve
 * "Laura Marinelli".
 */
function frasiDellaParte(t, titolare, squadra) {
    const n = t.length;
    let inizioTitolare = n;
    if (titolare) {
        for (let k = 1; k < n - 1; k++) {
            if (['di', 'del', 'della', 'dei'].includes(t[k]) && t.slice(k + 1, k + 4).some((x) => NOMI.has(x))) {
                inizioTitolare = k;
                break;
            }
        }
    }
    const proprio = (x) => NOMI.has(x) || squadra.has(x);
    const out = [];
    for (let i = 0; i < n; i++) {
        for (let j = i; j < n; j++) {
            const intera = i === 0 && j === n - 1;
            if (!intera && j - i >= 6)
                break;
            const inizio = t[i];
            const fine = t[j];
            if (VUOTE.has(fine) || fine === '&')
                continue;
            if ((VUOTE.has(inizio) || inizio === '&') && !(j > i && ARTICOLI_DEL_NOME.has(inizio)))
                continue;
            const frase = t.slice(i, j + 1);
            const contenuto = frase.filter((x) => !VUOTE.has(x) && x !== '&');
            if (contenuto.length === 1) {
                const x = contenuto[0];
                if (i === j) {
                    if (distintiva(x, squadra) && i < inizioTitolare)
                        out.push(frase);
                }
                else if (j === i + 1 && ARTICOLI_DEL_NOME.has(inizio) && !proprio(x) && x.length >= 3 && !/^\d+$/.test(x)) {
                    out.push(frase); // "la rosa", "the rebel", "de fiore"
                }
                continue;
            }
            if (!intera && contenuto.every((x) => GENERICHE.has(x) || /^\d+$/.test(x)))
                continue; // "centro medico"
            if (!intera && contenuto.every(proprio))
                continue; // "angelo maria"
            out.push(frase);
        }
    }
    return out;
}
/** Le forme attaccate di una frase: "emmequadro", "waterandcomplements" e "waterecomplements". */
function compattiDi(frase) {
    let varianti = [''];
    for (const x of frase)
        varianti = x === '&' ? varianti.flatMap((v) => [`${v}and`, `${v}e`]) : varianti.map((v) => v + x);
    return varianti.filter((v) => v.length >= 6);
}
function costruisciIndice(ctx) {
    const nomiSquadra = new Set();
    const persone = new Map();
    const squadra = [];
    const parolePiegate = (s) => tokenizza(piega(s.replace(SPAZI_STRANI, ' '))).filter((t) => t.tipo === 'p').map((t) => t.w).join(' ');
    for (const p of ctx.persone) {
        const nome = parolePiegate(p.firstName);
        const cognome = parolePiegate(p.lastName);
        squadra.push({ id: p.id, nome, cognome });
        for (const w of nome.split(' '))
            if (w)
                nomiSquadra.add(w);
        for (const k of new Set([nome, cognome, `${nome} ${cognome}`.trim(), `${cognome} ${nome}`.trim()])) {
            if (!k)
                continue;
            const ids = persone.get(k) ?? [];
            if (!ids.includes(p.id))
                ids.push(p.id);
            persone.set(k, ids);
        }
    }
    const frasi = new Map();
    const compatti = new Map();
    let maxParole = 1;
    const aggiungi = (frase, id) => {
        const k = frase.join(' ');
        (frasi.get(k) ?? frasi.set(k, new Set()).get(k)).add(id);
        for (const v of compattiDi(frase))
            (compatti.get(v) ?? compatti.set(v, new Set()).get(v)).add(id);
        maxParole = Math.max(maxParole, frase.length);
    };
    // Chi ha ogni parola nel nome: un nome di battesimo vale da solo solo se è di un cliente solo.
    const chiHa = new Map();
    const persona = [];
    for (const c of ctx.clienti) {
        const nomi = [c.legalName, c.shortName].filter((x) => !!x && !!x.trim());
        const visti = new Set();
        for (const nome of nomi) {
            const chiave = piega(nome).trim();
            if (visti.has(chiave))
                continue;
            visti.add(chiave);
            const { parti, prima } = partiDelNome(nome);
            for (const parte of parti) {
                for (const w of parte)
                    (chiHa.get(w) ?? chiHa.set(w, new Set()).get(w)).add(c.id);
                for (const frase of frasiDellaParte(parte, true, nomiSquadra))
                    aggiungi(frase, c.id);
            }
            // "Fausto Carbone", "Daria Sessa": un cliente che è una persona.
            const t = parti[0];
            if (!t || parti.length > 1 || !prima || t[0] !== prima || t.length < 2 || t.length > 4)
                continue;
            if (!NOMI.has(prima) || COMUNI.has(prima) || nomiSquadra.has(prima))
                continue;
            let cognome = false;
            let ok = true;
            for (let i = 1; i < t.length; i++) {
                const x = t[i];
                if (NOMI.has(x))
                    continue;
                if (PARTICELLE.has(x)) {
                    if (i + 1 < t.length) {
                        cognome = true;
                        i++;
                        continue;
                    }
                    ok = false;
                    break;
                }
                if (COMUNI.has(x) || GENERICHE.has(x) || CITTA.has(x) || /^\d+$/.test(x) || x.length < 2 || x === '&') {
                    ok = false;
                    break;
                }
                cognome = true;
            }
            if (ok && cognome)
                persona.push({ id: c.id, nome: prima });
        }
    }
    for (const p of persona)
        if (chiHa.get(p.nome)?.size === 1)
            aggiungi([p.nome], p.id);
    // Progetti: solo la parte prima di "—" ("Villa Fago — Digital Marketing 2026").
    for (const p of ctx.progetti ?? []) {
        if (!p.clientId || !p.name.includes('—'))
            continue;
        const testa = p.name.split('—')[0] ?? '';
        for (const parte of partiDelNome(testa).parti) {
            for (const frase of frasiDellaParte(parte, false, nomiSquadra))
                aggiungi(frase, p.clientId);
        }
    }
    return { frasi, compatti, maxParole, persone, squadra, nomiSquadra };
}
let ultimoIndice = null;
function indiceDi(ctx) {
    const u = ultimoIndice;
    if (u && u.clienti === ctx.clienti && u.progetti === ctx.progetti && u.persone === ctx.persone)
        return u.indice;
    const indice = costruisciIndice(ctx);
    ultimoIndice = { clienti: ctx.clienti, progetti: ctx.progetti, persone: ctx.persone, indice };
    return indice;
}
/** Le parole attaccate in tutti i modi: "homs"+"sapienza" vale anche "homsapienza". */
function unioni(parole) {
    let out = [parole[0] ?? ''];
    for (const p of parole.slice(1)) {
        out = out.flatMap((v) => (p && v.endsWith(p.charAt(0)) ? [v + p, v + p.slice(1)] : [v + p]));
    }
    return out;
}
function trovaClienti(toks, testo, ix) {
    const trovate = [];
    const parola = (t) => !!t && t.tipo !== 'd';
    const metti = (s, e, ids) => trovate.push({ s, e, ids: new Set(ids), testo: testo.slice(toks[s].s, toks[e - 1].e) });
    for (let i = 0; i < toks.length; i++) {
        if (!parola(toks[i]) || toks[i].c === '&')
            continue;
        for (let L = Math.min(ix.maxParole, toks.length - i); L >= 1; L--) {
            const pezzo = toks.slice(i, i + L);
            if (!pezzo.every(parola))
                continue;
            const ids = ix.frasi.get(pezzo.map((t) => t.c).join(' '));
            if (ids)
                metti(i, i + L, ids);
        }
        for (let L = 1; L <= Math.min(4, toks.length - i); L++) {
            const pezzo = toks.slice(i, i + L);
            if (!pezzo.every((t) => t.tipo === 'p' || t.tipo === 'n'))
                break;
            for (const v of new Set(unioni(pezzo.map((t) => t.w)))) {
                const ids = ix.compatti.get(v);
                if (ids)
                    metti(i, i + L, ids);
            }
        }
    }
    // Lo stesso pezzo trovato due volte è una menzione sola; una dentro una più lunga non conta
    // ("montagna comunicazione" sceglie fra le due Montagna).
    const perPezzo = new Map();
    for (const m of trovate) {
        const k = `${m.s}:${m.e}`;
        const g = perPezzo.get(k);
        if (g)
            for (const id of m.ids)
                g.ids.add(id);
        else
            perPezzo.set(k, m);
    }
    const lista = [...perPezzo.values()];
    return lista
        .filter((m) => !lista.some((o) => o !== m && o.s <= m.s && o.e >= m.e && o.e - o.s > m.e - m.s))
        .sort((a, b) => a.s - b.s);
}
function decidiCliente(menzioni) {
    if (!menzioni.length)
        return null;
    const omonimi = (m) => `${maiuscola(quanti(m.ids.size))} clienti si chiamano ${maiuscola(m.testo)}: scegli quale`;
    const uniche = menzioni.filter((m) => m.ids.size === 1);
    const ids = [];
    const nomi = [];
    for (const m of uniche) {
        const id = [...m.ids][0];
        if (!ids.includes(id)) {
            ids.push(id);
            nomi.push(maiuscola(m.testo));
        }
    }
    const dubbie = menzioni.filter((m) => m.ids.size > 1 && !ids.some((id) => m.ids.has(id)));
    if (ids.length === 1)
        return { id: ids[0], dubbio: dubbie[0] ? omonimi(dubbie[0]) : undefined };
    if (ids.length > 1)
        return { id: null, dubbio: `Nella riga ci sono ${quanti(ids.length)} clienti, ${elenco(nomi)}: scegli quale` };
    return { id: null, dubbio: omonimi(dubbie[0]) };
}
const GIORNO_MS = 86_400_000;
const pad2 = (n) => String(n).padStart(2, '0');
const isoDi = (g) => `${g.y}-${pad2(g.m)}-${pad2(g.d)}`;
const utc = (g) => Date.UTC(g.y, g.m - 1, g.d);
function daUtc(t) {
    const x = new Date(t);
    return { y: x.getUTCFullYear(), m: x.getUTCMonth() + 1, d: x.getUTCDate() };
}
const giorniNelMese = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const valida = (y, m, d) => m >= 1 && m <= 12 && d >= 1 && d <= giorniNelMese(y, m);
const piuGiorni = (g, n) => daUtc(utc(g) + n * GIORNO_MS);
const differenza = (a, b) => Math.round((utc(a) - utc(b)) / GIORNO_MS);
const giornoSettimana = (g) => new Date(utc(g)).getUTCDay();
function mesePiu(g, k) {
    const z = g.m - 1 + k;
    return { y: g.y + Math.floor(z / 12), m: (z % 12) + 1 };
}
function piuMesi(g, k) {
    const { y, m } = mesePiu(g, k);
    return { y, m, d: Math.min(g.d, giorniNelMese(y, m)) };
}
const lunediProssimo = (oggi) => piuGiorni(oggi, ((1 - giornoSettimana(oggi) + 7) % 7) || 7);
/** Venerdì di questa settimana; il sabato e la domenica, domenica. */
function fineSettimana(oggi) {
    const g = giornoSettimana(oggi);
    if (g >= 1 && g <= 5)
        return piuGiorni(oggi, 5 - g);
    return g === 6 ? piuGiorni(oggi, 1) : oggi;
}
const NOMI_MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const NOMI_GIORNI = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MESI = new Map([
    ...NOMI_MESI.map((m, i) => [m, i + 1]),
    ['gen', 1], ['feb', 2], ['mar', 3], ['apr', 4], ['mag', 5], ['giu', 6], ['lug', 7], ['ago', 8], ['sett', 9], ['ott', 10], ['nov', 11], ['dic', 12],
]);
const GIORNI = new Map([['domenica', 0], ['lunedi', 1], ['martedi', 2], ['mercoledi', 3], ['giovedi', 4], ['venerdi', 5], ['sabato', 6]]);
/** Le sigle valgono solo in testa alla riga, dopo "entro"/"per"/"x" o prima del numero del giorno ("lungo", "mare", "gioielli" no). */
const GIORNI_BREVI = new Map([['dom', 0], ['lun', 1], ['mar', 2], ['mer', 3], ['gio', 4], ['ven', 5], ['sab', 6]]);
const RELATIVI = new Map([
    ['oggi', 0], ['stasera', 0], ['stamattina', 0], ['stamani', 0], ['stamane', 0], ['stanotte', 0],
    ['domani', 1], ['domattina', 1], ['dmn', 1], ['dopodomani', 2],
]);
const MOMENTI = insieme('mattina mattino pomeriggio sera stesso');
const NUMERI = new Map([
    ['un', 1], ['uno', 1], ['una', 1], ['due', 2], ['tre', 3], ['quattro', 4], ['cinque', 5], ['sei', 6], ['sette', 7],
    ['otto', 8], ['nove', 9], ['dieci', 10], ['quindici', 15], ['venti', 20], ['trenta', 30],
]);
const UNITA = new Map([
    ['giorno', 1], ['giorni', 1], ['gg', 1], ['settimana', 7], ['settimane', 7], ['sett', 7], ['mese', 'mese'], ['mesi', 'mese'],
]);
/** Dopo un numero, queste parole dicono una quantità e non un giorno ("il 3 post", "il 5 per cento"). */
const QUANTITA = insieme(`
  giorni giorno gg ore ora minuti min settimane settimana mesi mese anni anno euro eur k mila volte
  post reel video foto pezzi copie persone pagine slide articoli caroselli stories storie clienti
  per percento cento`);
/** Formati, non date: "video 16/9", "carosello 4/5". */
const RAPPORTI = insieme('16/9 9/16 4/5 5/4 1/1 4/3 3/4 2/3 3/2 21/9 9/21 1/2 2/1');
function ilGiorno(d, m, y) {
    const articolo = d === 8 || d === 11 ? "L'" : 'Il ';
    return `${articolo}${d === 1 ? '1°' : d} ${NOMI_MESI[m - 1]}${y ? ` ${y}` : ''}`;
}
const confronta = (a, b) => utc(a) - utc(b);
/** Giorno e mese senza anno: il prossimo, oppure un dubbio se non esiste o è appena passato. */
function dataSenzaAnno(oggi, m, d) {
    const y = oggi.y;
    const ok1 = valida(y, m, d);
    const ok2 = valida(y + 1, m, d);
    if (!ok1 && !ok2) {
        const annoFeb = m === 2 && d === 29 ? (confronta(oggi, { y, m: 3, d: 1 }) < 0 ? y : y + 1) : undefined;
        return { g: null, dubbio: `${ilGiorno(d, m, annoFeb)} non esiste: scegli la data` };
    }
    const g1 = { y, m, d };
    const g2 = { y: y + 1, m, d };
    if (ok1 && confronta(g1, oggi) >= 0)
        return { g: g1 };
    // Il 15 gennaio scritto a ottobre è quello che viene; il 3 ottobre scritto il 7 è uno sbaglio.
    if (ok2 && differenza(g2, oggi) <= 183)
        return { g: g2 };
    if (ok1)
        return { g: g1, dubbio: `${ilGiorno(d, m)} è già passato: controlla la data` };
    return { g: g2, dubbio: `${ilGiorno(d, m, y + 1)} è tra più di sei mesi: controlla la data` };
}
function dataConAnno(oggi, y, m, d) {
    if (!valida(y, m, d))
        return { g: null, dubbio: `${ilGiorno(d, m, y)} non esiste: scegli la data` };
    const g = { y, m, d };
    if (confronta(g, oggi) < 0)
        return { g, dubbio: `${ilGiorno(d, m, y)} è già passato: controlla la data` };
    if (differenza(g, oggi) > 365)
        return { g, dubbio: `${ilGiorno(d, m, y)} è tra più di un anno: controlla la data` };
    return { g };
}
/** "il 5": in questo mese se non è passato, se no nel primo mese che ha quel giorno. */
function soloGiorno(oggi, d) {
    if (d === oggi.d)
        return { g: oggi };
    if (d > oggi.d) {
        if (valida(oggi.y, oggi.m, d))
            return { g: { y: oggi.y, m: oggi.m, d } };
        return { g: null, dubbio: `A ${NOMI_MESI[oggi.m - 1]} non c'è il ${d}: scegli la data` };
    }
    for (let k = 1; k <= 3; k++) {
        const { y, m } = mesePiu(oggi, k);
        if (valida(y, m, d))
            return { g: { y, m, d } };
    }
    return { g: null, dubbio: `Il ${d} non c'è nei prossimi mesi: scegli la data` };
}
/** "fine novembre": l'ultimo giorno del mese che viene. */
function fineDelMese(oggi, m, anno) {
    if (anno)
        return dataConAnno(oggi, anno, m, giorniNelMese(anno, m));
    const g1 = { y: oggi.y, m, d: giorniNelMese(oggi.y, m) };
    if (confronta(g1, oggi) >= 0)
        return { g: g1 };
    const g2 = { y: oggi.y + 1, m, d: giorniNelMese(oggi.y + 1, m) };
    if (differenza(g2, oggi) <= 183)
        return { g: g2 };
    return { g: g1, dubbio: `La fine di ${NOMI_MESI[m - 1]} è già passata: controlla la data` };
}
const seq = (s) => s.split(' ');
const perLunghezza = (a, b) => b.length - a.length;
/** Le preposizioni che vanno via insieme alla data ("entro e non oltre il 20", "per domani", "di venerdì"). */
const PREP = {
    rel: ['entro e non oltre', 'non oltre', 'prima di', 'fino a', 'a partire da', 'entro', 'per', 'x', 'da'].map(seq).sort(perLunghezza),
    sett: ['entro e non oltre il', 'entro e non oltre', 'non oltre il', 'non oltre', 'prima di', 'fino a', 'entro il', 'per il', 'x il',
        'entro', 'per', 'x', 'di', 'il', 'da', 'dal', 'al', 'a'].map(seq).sort(perLunghezza),
    num: ['entro e non oltre il giorno', 'entro e non oltre il', 'entro e non oltre l', 'entro e non oltre', 'non oltre il', 'non oltre l',
        'entro il giorno', 'per il giorno', 'x il giorno', 'prima del', 'prima dell', 'fino al', 'fino all', 'fino a', 'il giorno', 'di giorno',
        'del giorno', 'entro il', 'entro l', 'per il', 'per l', 'x il', 'x l', 'giorno', 'entro', 'per', 'x', 'il', 'l', 'del', 'dell',
        'dal', 'dall', 'al', 'all', 'di'].map(seq).sort(perLunghezza),
    fine: ['entro e non oltre la', 'entro la', 'per la', 'x la', 'verso la', 'entro', 'per', 'x', 'alla', 'verso', 'a', 'la', 'di'].map(seq).sort(perLunghezza),
    settimana: ['entro la', 'per la', 'x la', 'entro', 'per', 'x', 'la', 'nella'].map(seq).sort(perLunghezza),
    tra: [],
};
/** Davanti al numero del giorno da solo ci vuole uno di questi: "il 5", "entro l'8", "giorno 19". */
const ARTICOLI_DEL_GIORNO = insieme('il l del dell al all dal dall giorno');
/** Fra due parole solo spazi o l'apostrofo ("l'8"): sono attaccate. */
const attaccate = (norm, a, b) => /^[\s']*$/.test(norm.slice(a.e, b.s));
/** Fra giorno, mese e anno solo spazi (e il "°" di "1° novembre"): "10 (settembre)" e "3 - maggio" non sono date. */
const vicine = (norm, a, b) => !!a && !!b && /^[\s°]*$/.test(norm.slice(a.e, b.s));
/** Un anno scritto dopo il mese: "15 gennaio 2027" sì, "4 aprile 1000" (rata da 1000 euro) no. */
const annoDopo = (norm, prima, t) => t?.tipo === 'n' && t.w.length === 4 && Number(t.w) >= 2000 && Number(t.w) <= 2100 && vicine(norm, prima, t) ? Number(t.w) : null;
/** Dove comincia la data con le sue preposizioni davanti. */
function inizioConPreposizioni(toks, norm, s, lista, usati) {
    for (const p of lista) {
        const L = p.length;
        if (s - L < 0)
            continue;
        let ok = true;
        for (let k = 0; k < L && ok; k++) {
            const t = toks[s - L + k];
            ok = !usati[s - L + k] && t.w === p[k] && attaccate(norm, t, toks[s - L + k + 1]);
        }
        if (ok)
            return s - L;
    }
    return s;
}
function articoloPrima(toks, norm, i, usati) {
    const p = toks[i - 1];
    return !!p && !usati[i - 1] && ARTICOLI_DEL_GIORNO.has(p.w) && attaccate(norm, p, toks[i]);
}
function eQuantita(toks, norm, i) {
    const t = toks[i];
    const dopo = norm.charAt(t.e);
    if (dopo === '%' || dopo === '€' || ((dopo === ',' || dopo === '.') && /\d/.test(norm.charAt(t.e + 1))))
        return true;
    const n = toks[i + 1];
    return !!n && n.tipo === 'p' && QUANTITA.has(n.w) && /^\s*$/.test(norm.slice(t.e, n.s));
}
const numeroDa = (t) => {
    if (!t)
        return null;
    if (t.tipo === 'n') {
        const n = Number(t.w);
        return n >= 1 && n <= 400 ? n : null;
    }
    return NUMERI.get(t.w) ?? null;
};
const giornoDa = (t) => {
    if (!t || t.tipo !== 'n' || t.w.length > 2)
        return null;
    const n = Number(t.w);
    return n >= 1 && n <= 31 ? n : null;
};
/** Una data che comincia alla parola i (senza le preposizioni davanti). */
function dataQui(toks, norm, i, oggi, inTesta, usati) {
    const t = toks[i];
    const w = t.w;
    const at = (k) => toks[k]?.w ?? '';
    const libera = (k) => k < toks.length && !usati[k];
    // 20/12, 31/12/2026, 2026-10-20
    if (t.tipo === 'd') {
        if (t.aa != null)
            return { s: i, e: i + 1, tipo: 'num', ...dataConAnno(oggi, t.aa, t.mm, t.dd) };
        if (inTesta || inizioConPreposizioni(toks, norm, i, PREP.num, usati) < i) {
            return { s: i, e: i + 1, tipo: 'num', ...dataSenzaAnno(oggi, t.mm, t.dd) };
        }
        // Senza "il", "entro", "per" davanti è più spesso un formato ("video 16/9"): resta nel titolo.
        if (RAPPORTI.has(`${t.dd}/${t.mm}`))
            return null;
        if (!valida(oggi.y, t.mm, t.dd) && !valida(oggi.y + 1, t.mm, t.dd)) {
            return { s: i, e: i + 1, tipo: 'num', ...dataSenzaAnno(oggi, t.mm, t.dd), g: null };
        }
        return { s: i, e: i + 1, tipo: 'num', g: null, dubbio: `«${norm.slice(t.s, t.e)}» è una scadenza? Se sì, sceglila` };
    }
    // oggi, stasera, domani, domattina, dopodomani
    const rel = RELATIVI.get(w);
    if (rel !== undefined) {
        let e = i + 1;
        if (libera(e) && MOMENTI.has(at(e)))
            e++;
        return { s: i, e, tipo: 'rel', g: piuGiorni(oggi, rel) };
    }
    if (w === 'in' && at(i + 1) === 'giornata' && libera(i + 1))
        return { s: i, e: i + 2, tipo: 'rel', g: oggi };
    // tra 2 settimane, fra 3 giorni, entro una settimana, tra un paio di giorni
    if (w === 'tra' || w === 'fra' || w === 'entro') {
        let k = i + 1;
        let n;
        if (at(k) === 'un' && at(k + 1) === 'paio' && at(k + 2) === 'di') {
            n = 2;
            k += 3;
        }
        else {
            n = numeroDa(toks[k]);
            k++;
        }
        const u = UNITA.get(at(k));
        if (n !== null && u !== undefined && libera(k)) {
            return { s: i, e: k + 1, tipo: 'tra', g: u === 'mese' ? piuMesi(oggi, n) : piuGiorni(oggi, n * u) };
        }
    }
    // entro novembre: l'ultimo giorno del mese
    if (w === 'entro' && MESI.has(at(i + 1)) && libera(i + 1) && vicine(norm, t, toks[i + 1])) {
        let e = i + 2;
        const anno = annoDopo(norm, toks[e - 1], toks[e]);
        if (anno)
            e++;
        return { s: i, e, tipo: 'tra', ...fineDelMese(oggi, MESI.get(at(i + 1)), anno) };
    }
    // fine mese, fine anno, fine settimana, fine novembre
    if (w === 'finemese')
        return { s: i, e: i + 1, tipo: 'fine', g: { y: oggi.y, m: oggi.m, d: giorniNelMese(oggi.y, oggi.m) } };
    if (w === 'weekend' || w === 'finesettimana')
        return { s: i, e: i + 1, tipo: 'fine', g: fineSettimana(oggi) };
    if (w === 'fine') {
        let k = i + 1;
        if (['del', 'della', 'dell', 'di'].includes(at(k)))
            k++;
        if (at(k) === 'questo' || at(k) === 'questa')
            k++;
        const x = at(k);
        if (x === 'mese') {
            if (at(k + 1) === 'prossimo') {
                const { y, m } = mesePiu(oggi, 1);
                return { s: i, e: k + 2, tipo: 'fine', g: { y, m, d: giorniNelMese(y, m) } };
            }
            return { s: i, e: k + 1, tipo: 'fine', g: { y: oggi.y, m: oggi.m, d: giorniNelMese(oggi.y, oggi.m) } };
        }
        if (x === 'anno')
            return { s: i, e: k + 1, tipo: 'fine', g: { y: oggi.y, m: 12, d: 31 } };
        if (x === 'settimana')
            return { s: i, e: k + 1, tipo: 'fine', g: fineSettimana(oggi) };
        const m = MESI.get(x);
        if (m && vicine(norm, toks[k - 1], toks[k])) {
            let e = k + 1;
            const anno = annoDopo(norm, toks[e - 1], toks[e]);
            if (anno)
                e++;
            return { s: i, e, tipo: 'fine', ...fineDelMese(oggi, m, anno) };
        }
    }
    // settimana prossima, prossima settimana, in settimana, entro la settimana
    if ((w === 'settimana' && at(i + 1) === 'prossima') || (w === 'prossima' && at(i + 1) === 'settimana')) {
        return { s: i, e: i + 2, tipo: 'settimana', g: lunediProssimo(oggi) };
    }
    if ((w === 'in' || w === 'questa') && at(i + 1) === 'settimana' && at(i + 2) !== 'prossima') {
        return { s: i, e: i + 2, tipo: 'settimana', g: fineSettimana(oggi) };
    }
    if (w === 'entro' && at(i + 1) === 'la' && at(i + 2) === 'settimana' && at(i + 3) !== 'prossima') {
        return { s: i, e: i + 3, tipo: 'settimana', g: fineSettimana(oggi) };
    }
    // lunedì, venerdì prossimo, il prossimo martedì, lunedì 13, venerdì 9 ottobre
    {
        let k = i;
        if ((w === 'prossimo' || w === 'prossima') && libera(i + 1))
            k = i + 1;
        const gw = at(k);
        let gs = GIORNI.get(gw);
        if (gs === undefined && GIORNI_BREVI.has(gw)) {
            const n = toks[k + 1];
            const conNumero = !!n && (giornoDa(n) !== null || (n.tipo === 'd'));
            if (k > i || inTesta || conNumero || inizioConPreposizioni(toks, norm, k, PREP.sett, usati) < k)
                gs = GIORNI_BREVI.get(gw);
        }
        if (gs !== undefined && (k === i || GIORNI.has(gw) || GIORNI_BREVI.has(gw))) {
            let e = k + 1;
            let esito = null;
            const n = toks[e];
            if (n && libera(e) && n.tipo === 'd') {
                esito = n.aa != null ? dataConAnno(oggi, n.aa, n.mm, n.dd) : dataSenzaAnno(oggi, n.mm, n.dd);
                e++;
            }
            else if (n && libera(e) && giornoDa(n) !== null && vicine(norm, toks[k], n) && !eQuantita(toks, norm, e)) {
                const d = giornoDa(n);
                e++;
                const m = MESI.get(at(e));
                if (m && vicine(norm, n, toks[e])) {
                    e++;
                    const anno = annoDopo(norm, toks[e - 1], toks[e]);
                    if (anno)
                        e++;
                    esito = anno ? dataConAnno(oggi, anno, m, d) : dataSenzaAnno(oggi, m, d);
                }
                else
                    esito = soloGiorno(oggi, d);
            }
            if (esito) {
                if (esito.g && !esito.dubbio && giornoSettimana(esito.g) !== gs) {
                    esito = { g: esito.g, dubbio: `${ilGiorno(esito.g.d, esito.g.m)} è ${NOMI_GIORNI[giornoSettimana(esito.g)]}, non ${NOMI_GIORNI[gs]}: controlla la data` };
                }
            }
            else {
                let prossimaSettimana = false;
                for (const p of [['della', 'settimana', 'prossima'], ['della', 'prossima', 'settimana'], ['settimana', 'prossima'], ['prossima', 'settimana']]) {
                    if (p.every((x, j) => at(e + j) === x && libera(e + j))) {
                        e += p.length;
                        prossimaSettimana = true;
                        break;
                    }
                }
                if (!prossimaSettimana && (at(e) === 'prossimo' || at(e) === 'prossima') && libera(e))
                    e++;
                // Lo stesso giorno di oggi è fra sette giorni: "mercoledì" scritto di mercoledì.
                const g = prossimaSettimana
                    ? piuGiorni(lunediProssimo(oggi), (gs + 6) % 7)
                    : piuGiorni(oggi, ((gs - giornoSettimana(oggi) + 7) % 7) || 7);
                esito = { g };
            }
            if (libera(e) && MOMENTI.has(at(e)) && at(e) !== 'stesso')
                e++;
            return { s: i, e, tipo: 'sett', ...esito };
        }
    }
    // 15 gennaio, il 3 ottobre, 8 ott, il primo novembre; il 5, entro l'8
    if (giornoDa(t) !== null || w === 'primo') {
        const d = w === 'primo' ? 1 : giornoDa(t);
        // "rata 2 di 4 aprile": il 4 conta le rate, non è il giorno
        const frazione = at(i - 1) === 'di' && toks[i - 2]?.tipo === 'n';
        let k = i + 1;
        if (at(k) === 'di' && vicine(norm, t, toks[k]))
            k++;
        const m = MESI.get(at(k));
        if (m && libera(k) && !frazione && vicine(norm, toks[k - 1], toks[k])) {
            let e = k + 1;
            const anno = annoDopo(norm, toks[e - 1], toks[e]);
            if (anno)
                e++;
            return { s: i, e, tipo: 'num', ...(anno ? dataConAnno(oggi, anno, m, d) : dataSenzaAnno(oggi, m, d)) };
        }
        if (w !== 'primo' && articoloPrima(toks, norm, i, usati) && !eQuantita(toks, norm, i)) {
            return { s: i, e: i + 1, tipo: 'num', ...soloGiorno(oggi, d) };
        }
    }
    return null;
}
function trovaDate(toks, norm, oggi, usati) {
    const out = [];
    for (let i = 0; i < toks.length; i++) {
        if (usati[i])
            continue;
        const inTesta = usati.slice(0, i).every(Boolean);
        const r = dataQui(toks, norm, i, oggi, inTesta, usati);
        if (!r)
            continue;
        const s = r.g ? inizioConPreposizioni(toks, norm, r.s, PREP[r.tipo], usati) : r.s;
        for (let k = s; k < r.e; k++)
            usati[k] = true;
        out.push({ ...r, s });
        i = r.e - 1;
    }
    return out;
}
// ── Urgenza ───────────────────────────────────────────────────────────────
const NEGAZIONI = [
    'non e urgente', 'non e urgentissimo', 'non e urgentissima', 'non e prioritario', 'non e prioritaria', 'non c e urgenza',
    'non urgente', 'non urgenti', 'non urgentissimo', 'non urge', 'non ce urgenza', 'non prioritario', 'non prioritaria',
    'nessuna urgenza', 'senza urgenza', 'niente urgenza', 'priorita bassa', 'bassa priorita', 'priorita normale',
].map(seq).sort(perLunghezza);
const URGENZE = [
    'il prima possibile', 'al piu presto', 'prima possibile', 'appena possibile', 'priorita alta', 'alta priorita',
    'massima priorita', 'priorita massima', 'priorita urgente', 'con urgenza', 'd urgenza', 'massima urgenza',
    'urgente', 'urgenti', 'urgentissimo', 'urgentissima', 'urgentissimi', 'urgentissime', 'urgentemente', 'urg', 'urge', 'asap',
].map(seq).sort(perLunghezza);
function trovaUrgenza(toks, norm, testo, usati) {
    const via = [];
    let si = false;
    let no = false;
    const combacia = (i, p) => p.every((x, k) => toks[i + k]?.w === x && !usati[i + k] && (k === 0 || attaccate(norm, toks[i + k - 1], toks[i + k])));
    for (let i = 0; i < toks.length; i++) {
        if (usati[i])
            continue;
        const neg = NEGAZIONI.find((p) => combacia(i, p));
        const pos = neg ? undefined : URGENZE.find((p) => combacia(i, p));
        const p = neg ?? pos;
        if (!p)
            continue;
        let s = i;
        const e = i + p.length;
        // "è urgente": l'accento dice che non è la "e" di un elenco
        if (pos && toks[i - 1]?.w === 'e' && !usati[i - 1] && /[èé]/i.test(testo.slice(toks[i - 1].s, toks[i - 1].e + 1)))
            s = i - 1;
        for (let k = s; k < e; k++)
            usati[k] = true;
        let a = toks[s].s;
        let b = toks[e - 1].e;
        while (norm.charAt(b) === '!')
            b++;
        // "(urgente)": via anche le parentesi
        const primaA = norm.slice(0, a).match(/\(\s*$/);
        const dopoB = norm.slice(b).match(/^\s*\)/);
        if (primaA && dopoB) {
            a -= primaA[0].length;
            b += dopoB[0].length;
        }
        via.push([a, b]);
        if (neg)
            no = true;
        else
            si = true;
        i = e - 1;
    }
    if (!si && !no)
        return { alta: null, via };
    if (si && no)
        return { alta: false, via, dubbio: 'La riga dice sia urgente sia non urgente: scegli la priorità' };
    return { alta: si, via };
}
/** Un nome che comincia alla parola i: di una persona (ids) o un nome qualsiasi (ids vuoto). */
function nomeQui(toks, i, ix, ctx, usati) {
    for (let L = Math.min(4, toks.length - i); L >= 1; L--) {
        const pezzo = toks.slice(i, i + L);
        if (pezzo.some((t, k) => t.tipo !== 'p' || usati[i + k]))
            continue;
        const ids = ix.persone.get(pezzo.map((t) => t.w).join(' '));
        if (ids)
            return { e: i + L, ids };
    }
    const t = toks[i];
    if (!t || usati[i] || t.tipo !== 'p')
        return null;
    if (t.w === 'me' || t.w === 'io')
        return { e: i + 1, ids: [ctx.io] };
    if (NOMI.has(t.w))
        return { e: i + 1, ids: [] };
    return null;
}
function trovaPersona(toks, norm, testo, ix, ctx, usati, menzioni) {
    const segnali = [];
    const diCliente = (n) => menzioni.some((m) => m.s <= n.s && m.e >= n.e);
    const nome = (s, e, ids) => ({ s, e, ids, testo: testo.slice(toks[s].s, toks[e - 1].e) });
    // All'inizio: "x lorenzo:", "lorenzo:", "lorenzo deve", "Andrea,", "lorenzo e masha:"
    let i = 0;
    while (i < toks.length && usati[i])
        i++;
    const i0 = i;
    let prefisso = false;
    if ((toks[i]?.w === 'x' || toks[i]?.w === 'per') && i + 1 < toks.length) {
        prefisso = true;
        i++;
    }
    const nomi = [];
    while (i < toks.length) {
        const n = nomeQui(toks, i, ix, ctx, usati);
        if (!n)
            break;
        nomi.push(nome(i, n.e, n.ids));
        i = n.e;
        const t = toks[i];
        if (t?.c === '&' && nomeQui(toks, i + 1, ix, ctx, usati)) {
            i++;
            continue;
        }
        if (t && /^\s*,\s*$/.test(norm.slice(toks[i - 1].e, t.s)) && nomeQui(toks, i, ix, ctx, usati))
            continue;
        break;
    }
    if (nomi.length) {
        const ultimo = toks[i - 1];
        const resto = norm.slice(ultimo.e, toks[i]?.s ?? norm.length);
        let a = -1;
        const punt = /^\s*(?:[:;,]|[-–—](?=\s))/.exec(resto);
        if (punt)
            a = ultimo.e + punt[0].length;
        else if (toks[i]?.w === 'deve' || toks[i]?.w === 'devono')
            a = toks[i].e;
        else if (i >= toks.length && /^[\s.!]*$/.test(resto))
            a = norm.length;
        else if (prefisso)
            a = ultimo.e;
        // "fausto: montare il reel", "marco toscano: preventivo": è il nome di un cliente, vince il cliente.
        if (a >= 0 && !nomi.some(diCliente))
            segnali.push({ nomi, da: toks[i0].s, a });
    }
    // In fondo: "(lorenzo)", "(lorenzo e masha)"
    const fin = /\(([^()]{1,60})\)[\s.!]*$/.exec(norm);
    if (fin) {
        const a = fin.index + 1;
        const b = a + fin[1].length;
        const dentro = toks.map((t, k) => ({ t, k })).filter(({ t }) => t.s >= a && t.e <= b).map(({ k }) => k);
        const nomiF = [];
        let k = dentro[0] ?? -1;
        const ultimo = dentro[dentro.length - 1] ?? -1;
        let ok = k >= 0;
        while (ok && k <= ultimo) {
            const n = nomeQui(toks, k, ix, ctx, usati);
            if (!n || n.e - 1 > ultimo) {
                ok = false;
                break;
            }
            nomiF.push(nome(k, n.e, n.ids));
            k = n.e;
            if (k <= ultimo && toks[k].c === '&')
                k++;
        }
        if (ok && nomiF.length && !nomiF.some(diCliente))
            segnali.push({ nomi: nomiF, da: fin.index, a: norm.length });
    }
    // Ovunque: "@lore", "@andrea"
    for (const m of norm.matchAll(/(^|[\s(,;:])@([a-z0-9])/g)) {
        const at = (m.index ?? 0) + m[1].length;
        const k = toks.findIndex((t) => t.s === at + 1);
        if (k < 0 || usati[k] || toks[k].tipo !== 'p')
            continue;
        const w = toks[k].w;
        let ids = ix.persone.get(w) ?? [];
        if (!ids.length && w.length >= 3)
            ids = ix.squadra.filter((p) => p.nome.startsWith(w) || p.cognome.startsWith(w)).map((p) => p.id);
        if (!ids.length && (w === 'me' || w === 'io'))
            ids = [ctx.io];
        segnali.push({ nomi: [{ s: k, e: k + 1, ids, testo: `@${testo.slice(toks[k].s, toks[k].e)}` }], da: at, a: toks[k].e });
    }
    const etichette = segnali.map((s) => [s.da, s.a]);
    if (!segnali.length)
        return { campo: null, via: [], etichette, tok: [] };
    const tutti = segnali.flatMap((s) => s.nomi);
    const chiave = (n) => (n.ids.length ? [...n.ids].sort().join(',') : piega(n.testo).replace(/^@/, ''));
    const diversi = [...new Map(tutti.map((n) => [chiave(n), n])).values()];
    const nessunTolto = { via: [], etichette, tok: [] };
    if (diversi.length > 1) {
        const chi = elenco(diversi.map((n) => maiuscola(n.testo)));
        return { campo: { id: null, dubbio: `Nella riga ci sono più persone, ${chi}: scegli chi lo fa` }, ...nessunTolto };
    }
    const n = diversi[0];
    if (n.ids.length === 0) {
        return { campo: { id: null, dubbio: `Non trovo ${maiuscola(n.testo)} fra le persone: scegli chi lo fa` }, ...nessunTolto };
    }
    const via = segnali.map((s) => [s.da, s.a]);
    if (n.ids.length > 1) {
        return { campo: { id: null, dubbio: `Ci sono ${quanti(n.ids.length)} ${maiuscola(n.testo.replace(/^@/, ''))}: scegli chi` }, via, etichette, tok: tutti };
    }
    return { campo: { id: n.ids[0] }, via, etichette, tok: tutti };
}
const RIEMPITIVI = new Set([...VUOTE, 'x', 'per', 'da', 'fare', 'cose', 'task', 'todo', 'cliente', 'clienti', 'tutti', 'tutte', 'tutto', 'poi']);
// Le parole rimaste appese in fondo quando si toglie la data ("…per", "…entro il"). Non "in" né "su":
// "check in", "log in" finiscono così anche scritti a mano.
const ORFANE_IN_FONDO = insieme('entro per x il lo la l i gli le del della dello dell di a al alla all ad da dal dalla e ed o tra fra con prima fino non oltre');
/** Il titolo senza i pezzi tolti, con la punteggiatura rimasta appesa ripulita. */
function rifinisci(s, tagliatoInFondo, tagliatoInTesta) {
    let t = s.replace(/\s+/g, ' ')
        .replace(/\(\s*\)|\[\s*\]/g, ' ')
        .replace(/\s+([,;:.!?)\]])/g, '$1')
        .replace(/([([])\s+/g, '$1')
        .replace(/\s+/g, ' ')
        .trim();
    for (let giro = 0; giro < 8; giro++) {
        const prima = t;
        t = t.replace(/^[\s:;,.\-–—!?)\]|/'"]+/, '').replace(/[\s:;,\-–—([|/!?'"]+$/, '');
        if (/[^.]\.$/.test(t))
            t = t.slice(0, -1);
        if (tagliatoInFondo) {
            const m = /(^|\s)(\S+)$/.exec(t);
            if (m && m.index > 0 && ORFANE_IN_FONDO.has(piega(m[2])))
                t = t.slice(0, m.index);
        }
        if (tagliatoInTesta)
            t = t.replace(/^(?:ma|però|pero|e|ed|poi)\s+(?:(?:va|è|e)\s+(?:fatto|fatta|da\s+fare)\b)?\s*[:,\-–]?\s*/i, '');
        if (t === prima)
            break;
    }
    return t;
}
function accorcia(t) {
    if (t.length <= TITOLO_MAX)
        return { titolo: t, accorciato: false };
    let taglio = t.slice(0, TITOLO_MAX - 1);
    const spazio = taglio.lastIndexOf(' ');
    if (spazio > TITOLO_MAX * 0.6)
        taglio = taglio.slice(0, spazio);
    taglio = rifinisci(taglio, true, false);
    return { titolo: `${taglio}…`, accorciato: true };
}
function analizzaRiga(testo, ctx, ix, oggi) {
    const norm = piega(testo);
    const toks = tokenizza(norm);
    const usati = toks.map(() => false);
    const via = [];
    const etichette = [];
    const altri = [];
    const spanDi = (s, e) => {
        let b = toks[e - 1].e;
        if (norm.charAt(b) === "'")
            b++; // "lunedi'"
        else if (norm.charAt(b) === '.' && /^[a-z]{2,4}$/.test(toks[e - 1].w))
            b++; // "ven.", "8 ott."
        return [toks[s].s, b];
    };
    // Date
    const date = trovaDate(toks, norm, oggi, usati);
    let data = null;
    if (date.length) {
        const scelta = date.find((d) => d.g) ?? date[0];
        data = { iso: scelta.g ? isoDi(scelta.g) : null, dubbio: scelta.dubbio };
        if (scelta.g)
            via.push(spanDi(scelta.s, scelta.e));
        for (const d of date) {
            etichette.push(spanDi(d.s, d.e));
            if (d === scelta)
                continue;
            if (d.dubbio)
                altri.push(d.dubbio);
            if (d.g && scelta.g && isoDi(d.g) !== isoDi(scelta.g))
                altri.push('Nella riga ci sono due date: vale la prima, controlla');
        }
    }
    // Urgenza
    const urg = trovaUrgenza(toks, norm, testo, usati);
    via.push(...urg.via);
    etichette.push(...urg.via);
    // Clienti (non tolgono niente dal titolo) e persone
    let menzioni = trovaClienti(toks, testo, ix).filter((m) => !usati.slice(m.s, m.e).some(Boolean));
    const pers = trovaPersona(toks, norm, testo, ix, ctx, usati, menzioni);
    via.push(...pers.via);
    etichette.push(...pers.etichette);
    if (pers.tok.length)
        menzioni = menzioni.filter((m) => !pers.tok.some((n) => m.s < n.e && n.s < m.e));
    const cliente = decidiCliente(menzioni);
    // Titolo
    const tolto = new Array(testo.length).fill(false);
    for (const [a, b] of via)
        for (let k = a; k < b && k < testo.length; k++)
            tolto[k] = true;
    let grezzo = '';
    for (let k = 0; k < testo.length; k++) {
        if (!tolto[k])
            grezzo += testo.charAt(k);
        else if (k === 0 || !tolto[k - 1])
            grezzo += ' ';
    }
    const primoSegno = testo.search(/[\p{L}\p{N}]/u);
    let ultimoSegno = -1;
    for (let k = testo.length - 1; k >= 0; k--)
        if (/[\p{L}\p{N}]/u.test(testo.charAt(k))) {
            ultimoSegno = k;
            break;
        }
    const titolo = rifinisci(grezzo, via.some(([, b]) => b > ultimoSegno), via.some(([a]) => a <= primoSegno));
    // Dice solo questo? (data, persona, cliente, urgenza, più parole vuote)
    const coperto = (k) => etichette.some(([a, b]) => toks[k].s >= a && toks[k].e <= b) || menzioni.some((m) => k >= m.s && k < m.e);
    const resto = toks.filter((t, k) => !coperto(k) && !RIEMPITIVI.has(t.w) && t.c !== '&');
    const qualcosa = date.length > 0 || urg.alta !== null || !!pers.campo || menzioni.length > 0;
    return {
        testo,
        titolo,
        soloEtichette: qualcosa && resto.length === 0,
        cliente,
        persona: pers.campo,
        data,
        alta: urg.alta,
        dubbiUrgenza: urg.dubbio ? [urg.dubbio] : [],
        altri,
    };
}
// ── Il testo intero ───────────────────────────────────────────────────────
const RE_PUNTINO = /^\s*(?:(?:[-*•–—+·▪◦‣⁃>☐☑☒✓✔✗✘]+|\d{1,3}[.)])(?=\s|\p{L}|\[)|\p{Extended_Pictographic}\p{M}*)\s*/u;
const RE_CASELLA = /^\[[ xX✓✔]?\]\s*/u;
function togliPuntino(riga) {
    let s = riga;
    for (let giro = 0; giro < 3; giro++) {
        const prima = s;
        s = s.replace(RE_PUNTINO, '').replace(RE_CASELLA, '');
        if (s === prima)
            break;
    }
    return s.trim();
}
function daIso(s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m)
        return null;
    const g = { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
    return valida(g.y, g.m, g.d) ? g : null;
}
/** Una riga di testa dopo l'altra ("domani:" e poi "lorenzo:"): valgono tutte e due. */
function unisciTeste(prima, dopo) {
    return {
        ...dopo,
        cliente: dopo.cliente ?? prima.cliente,
        persona: dopo.persona ?? prima.persona,
        data: dopo.data ?? prima.data,
        alta: dopo.alta ?? prima.alta,
        dubbiUrgenza: dopo.alta !== null ? dopo.dubbiUrgenza : prima.dubbiUrgenza,
        altri: [...prima.altri, ...dopo.altri],
    };
}
/** Due righe di testa si sommano solo se dicono cose diverse: "Villa Fago" e poi "Raiontech" sono due righe, non una. */
const sommabili = (a, b) => !(a.cliente && b.cliente) && !(a.persona && b.persona) && !(a.data && b.data) && !(a.alta !== null && b.alta !== null);
function compone(r, testa, orfana) {
    const cliente = r.cliente ?? testa?.cliente ?? null;
    const persona = r.persona ?? testa?.persona ?? null;
    const data = r.data ?? testa?.data ?? null;
    const alta = r.alta ?? testa?.alta ?? null;
    const dubbi = [
        cliente?.dubbio, persona?.dubbio, data?.dubbio,
        ...(r.alta !== null ? r.dubbiUrgenza : testa?.dubbiUrgenza ?? []),
        ...r.altri,
    ].filter((d) => !!d);
    let titolo = r.titolo;
    if (orfana || !titolo)
        titolo = rifinisci(r.testo, false, false);
    const corto = accorcia(maiuscola(titolo));
    if (corto.accorciato)
        dubbi.push(DUBBIO_TITOLO_ACCORCIATO);
    if (orfana)
        dubbi.push(DUBBIO_MANCA_COSA_FARE);
    return {
        testo: r.testo,
        titolo: corto.titolo,
        clientId: cliente?.id ?? null,
        assigneeId: persona?.id ?? null,
        dueDate: data?.iso ?? null,
        alta: alta ?? false,
        dubbi: [...new Set(dubbi)],
    };
}
/**
 * Quante righe scritte ci sono (senza le vuote e quelle col solo puntino): la
 * finestra Nuovo task passa all'anteprima da due in su, e un incolla di due o
 * più righe nell'aggiunta veloce apre la finestra invece di creare un task solo.
 */
export function righeScritte(testo) {
    return (testo ?? '').replace(/\r\n?|[\p{Zl}\p{Zp}]/gu, '\n').split('\n')
        .filter((r) => /[\p{L}\p{N}]/u.test(togliPuntino(r.replace(SPAZI_STRANI, ' ')))).length;
}
/**
 * Divide il testo in task, uno per riga. Le righe vuote, i puntini d'elenco e
 * le righe di testa ("domani:", "lorenzo:") non diventano task.
 */
export function dividiTesto(testo, ctx) {
    const ix = indiceDi(ctx);
    const oggi = daIso(ctx.oggi) ?? daIso(oggiARoma());
    const out = [];
    // Le righe di testa in vigore ("domani:", poi "lorenzo:") e se hanno già avuto righe sotto.
    let teste = [];
    let testaUsata = false;
    const testaUnita = () => (teste.length ? teste.reduce(unisciTeste) : null);
    // Una testa senza righe sotto diventa un task da guardare, senza spunta: niente sparisce in silenzio.
    const chiudiTesta = () => {
        if (!testaUsata)
            for (const t of teste)
                out.push(compone(t, null, true));
        teste = [];
        testaUsata = false;
    };
    for (const grezza of (testo ?? '').replace(/\r\n?|[\p{Zl}\p{Zp}]/gu, '\n').split('\n')) {
        const riga = togliPuntino(grezza.replace(SPAZI_STRANI, ' ').replace(INVISIBILI, ''));
        if (!/[\p{L}\p{N}]/u.test(riga)) {
            // Una riga vuota chiude la testa che ha già avuto le sue righe.
            if (testaUsata) {
                teste = [];
                testaUsata = false;
            }
            continue;
        }
        const r = analizzaRiga(riga, ctx, ix, oggi);
        if (r.soloEtichette) {
            const unita = testaUnita();
            if (unita && !testaUsata && sommabili(unita, r))
                teste.push(r);
            else {
                chiudiTesta();
                teste = [r];
            }
            continue;
        }
        out.push(compone(r, testaUnita(), false));
        if (teste.length)
            testaUsata = true;
    }
    chiudiTesta();
    return out;
}
//# sourceMappingURL=dividi.js.map